import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  IconButton,
  Tooltip,
  useDisclosure,
  useToast,
  Text,
  HStack,
} from '@chakra-ui/react';

import { EditIcon } from '@chakra-ui/icons';

import DateField from 'components/fields/DateField';

import { apiUpdateCallListItem } from 'api';

import type { APICallListData } from 'types/api.types';

const ItemEffectiveDate = ({
  item,
  onAction,
}: {
  item: APICallListData;
  onAction?: () => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [effectiveDate, setEffectiveDate] = useState(
    item.effective_date ? new Date(item.effective_date) : null,
  );
  const [originalDate] = useState(
    item.effective_date ? new Date(item.effective_date) : null,
  );
  const toast = useToast();

  const updateEffectiveDateMutation = useMutation({
    mutationFn: () => {
      return apiUpdateCallListItem(item.id, {
        effective_date: effectiveDate
          ? effectiveDate.toISOString().split('T')[0]
          : null,
      });
    },
    onSuccess: (data) => {
      item.effective_date = data.effective_date;

      toast({
        title: 'Effective date updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      if (onAction) {
        onAction();
      }

      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error updating effective date',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const handleSave = () => {
    if (
      (effectiveDate === null && originalDate === null) ||
      (effectiveDate &&
        originalDate &&
        effectiveDate.getTime() === originalDate.getTime())
    ) {
      onClose();
      return;
    }

    updateEffectiveDateMutation.mutate();
  };

  const formattedDate = item.effective_date
    ? new Date(item.effective_date).toLocaleDateString()
    : 'Not set';

  return (
    <>
      <HStack spacing={2}>
        <Text>{formattedDate}</Text>
        <Tooltip label="Edit Effective Date" hasArrow placement="top">
          <IconButton
            aria-label="Edit effective date"
            icon={<EditIcon />}
            size="sm"
            colorScheme="teal"
            variant="ghost"
            onClick={onOpen}
          />
        </Tooltip>
      </HStack>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Effective Date</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <DateField
              label="Effective Date"
              date={effectiveDate}
              onChange={setEffectiveDate}
              htmlID="effective-date"
            />
          </ModalBody>

          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              isDisabled={updateEffectiveDateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={updateEffectiveDateMutation.isPending}
              loadingText="Saving..."
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ItemEffectiveDate;
